import React, { useState, useCallback, useEffect } from "react";
import { Stack, Box, Typography, LinearProgress, Grid, TextField, Snackbar, Alert, Autocomplete, Button, CircularProgress } from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { getData, putData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";

function EmploymentDetails({ navigationContext, onUpdateEmployeeSuccess, onSaveAndReturn }) {
    const [searchParams] = useSearchParams();
    const empIdFromUrl = searchParams.get("employeeId");
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const orgId = userData?.organization_id;

    const [completion, setCompletion] = useState(0);
    // Removed unused 'loading' state
    const [locations, setLocations] = useState([]);
    const [employmentTypes, setEmploymentTypes] = useState([]);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [teams, setTeams] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [managersList, setManagers] = useState([]);
    const [isFetchingUser, setIsFetchingUser] = useState(false);
    const [loadingDepts, setLoadingDepts] = useState(false);
    // Removed unused 'loadingTeams' and 'loadingDesignations' states

    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [formData, setFormData] = useState({
        dateOfJoining: null,
        employmentTypeId: '',
        department: '',
        team: '',
        designation: '',
        reportingManager: '',
        workLocation: '',
        roleId: '',
    });

    const getOptionById = (options, id) => {
        return options.find(option => String(option.id) === String(id)) || null;
    };

    const mapDataToForm = useCallback((user) => {
        if (!user) return;
        setFormData({
            dateOfJoining: user.doj ? dayjs(user.doj) : null,
            employmentTypeId: user.employment_type_id || '',
            department: user.department_id || '',
            team: user.team_id || '',
            designation: user.designation_id || '',
            reportingManager: user.report_manager_id || '',
            workLocation: user.location_id || '',
            roleId: user.role_id || '',
        });
    }, []);

    const fetchUserDetails = useCallback(async () => {
        if (!empIdFromUrl) return;
        setIsFetchingUser(true);
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${empIdFromUrl}`);
            if (response?.status === "SUCCESS") {
                mapDataToForm(response.message.user);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to fetch user data", severity: "error" });
        } finally {
            setIsFetchingUser(false);
        }
    }, [empIdFromUrl, mapDataToForm]);

    const fetchLocationsByOrganizationId = useCallback(async () => {
        if (!orgId) return;
        const response = await getData(API_ENDPOINTS.fetchLocationsByOrganizationId(orgId));
        if (response?.status === "SUCCESS") setLocations(response.message || []);
    }, [orgId]);

    const fetchAllEmploymentTypes = useCallback(async () => {
        if (!orgId) return;
        const response = await getData(API_ENDPOINTS.getAllEmploymentTypes(orgId));
        if (response.status === 'SUCCESS') setEmploymentTypes(response.result || []);
    }, [orgId]);

    const fetchOrgRoles = useCallback(async () => {
        if (!orgId) return;
        const response = await getData(API_ENDPOINTS.fetchRolesByOrganizationId(orgId));
        if (response.status === 'SUCCESS') setRoles(response.result || []);
    }, [orgId]);

    const fetchOrgDepartments = useCallback(async () => {
        if (!orgId) return;
        setLoadingDepts(true);
        const res = await getData(`${API_ENDPOINTS.fetchDepartmentsByOrganizationId}/${orgId}`);
        if (res?.status === "SUCCESS") setDepartments(res.message.departments || []);
        setLoadingDepts(false);
    }, [orgId]);

    const fetchTeamsByDepartmentId = useCallback(async (deptId) => {
        const res = await getData(`${API_ENDPOINTS.fetchTeamsByDepartmentId}/${deptId}`);
        setTeams(res?.status === "SUCCESS" ? res.message.teams : []);
    }, []);

    const fetchDesignationsByTeamId = useCallback(async (teamId) => {
        const url = typeof API_ENDPOINTS.fetchDesignationsByTeamId === 'function'
            ? API_ENDPOINTS.fetchDesignationsByTeamId(teamId)
            : `${API_ENDPOINTS.fetchDesignationsByTeamId}/${teamId}`;
        const res = await getData(url);
        setDesignations(res?.status === "SUCCESS" ? res.message.designations : []);
    }, []);

    const fetchReportingManagers = useCallback(async () => {
        if (!orgId) return;
        const res = await getData(API_ENDPOINTS.fetchTeamLeadsAndManagers(orgId));
        if (res?.status === "SUCCESS") {
            setManagers(res.message.users.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name} (${u.user_type})` })));
        }
    }, [orgId]);

    useEffect(() => {
        fetchUserDetails(); fetchLocationsByOrganizationId(); fetchAllEmploymentTypes();
        fetchOrgRoles(); fetchOrgDepartments(); fetchReportingManagers();
    }, [fetchUserDetails, fetchLocationsByOrganizationId, fetchAllEmploymentTypes, fetchOrgRoles, fetchOrgDepartments, fetchReportingManagers]);

    useEffect(() => {
        if (formData.department) fetchTeamsByDepartmentId(formData.department);
        else setTeams([]);
    }, [formData.department, fetchTeamsByDepartmentId]);

    useEffect(() => {
        if (formData.team) fetchDesignationsByTeamId(formData.team);
        else setDesignations([]);
    }, [formData.team, fetchDesignationsByTeamId]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.dateOfJoining) newErrors.dateOfJoining = "Joining date is required";
        if (!formData.employmentTypeId) newErrors.employmentTypeId = "Employment type is required";
        if (!formData.roleId) newErrors.roleId = "User role is required";
        if (!formData.department) newErrors.department = "Department is required";
        if (!formData.designation) newErrors.designation = "Designation is required";
        if (!formData.reportingManager) newErrors.reportingManager = "Reporting manager is required";
        if (!formData.workLocation) newErrors.workLocation = "Work location is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        const requiredFields = ['dateOfJoining', 'employmentTypeId', 'roleId', 'department', 'designation', 'reportingManager', 'workLocation'];
        const filledFields = requiredFields.filter(field => {
            if (field === 'dateOfJoining') return formData[field] !== null;
            return !!formData[field];
        });
        setCompletion(Math.round((filledFields.length / requiredFields.length) * 100));
    }, [formData]);

    const handleAutocompleteChange = (name, newValue) => {
        setFormData(prev => {
            const updated = { ...prev, [name]: newValue ? newValue.id : '' };
            if (name === 'department') { updated.team = ''; updated.designation = ''; }
            if (name === 'team') { updated.designation = ''; }
            return updated;
        });
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: false }));
    };


    const handleSaveChanges = async () => {
        if (!validateForm()) {
            setSnackbar({
                open: true,
                message: 'Please fill in all required fields',
                severity: 'error'
            });
            return;
        }

        const payload = {
            report_manager_id: formData.reportingManager,
            department_id: formData.department,
            team_id: formData.team,
            designation_id: formData.designation,
            doj: formData.dateOfJoining ? formData.dateOfJoining.toISOString() : null,
            role_id: formData.roleId,
            location_id: formData.workLocation
        };

        try {
            const response = await putData(API_ENDPOINTS.updateEmployeeProfile(empIdFromUrl), payload);

            // Accessing status from response.data based on your provided JSON structure
            if (response?.status === 'SUCCESS') {
                setSnackbar({
                    open: true,
                    message: response.message.message || 'Employment details updated successfully.',
                    severity: 'success'
                });
                await fetchUserDetails();
                if (onUpdateEmployeeSuccess) onUpdateEmployeeSuccess();
                if (navigationContext?.returnToRehire && onSaveAndReturn) onSaveAndReturn();
            } else {
                // Handle cases where status is not SUCCESS but request didn't throw
                setSnackbar({
                    open: true,
                    message: response?.data?.message?.message || 'Failed to update details',
                    severity: 'warning'
                });
            }
        } catch (error) {
            // Handle actual network or server errors
            const errorMessage = error.response?.data?.message?.message || 'Error while updating details';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 1 }}>
                {isFetchingUser && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 10,
                            borderRadius: '8px'
                        }}
                    >
                        <CircularProgress size={40} thickness={4} />
                    </Box>
                )}
                <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', my: 2 }}>
                    <Box sx={{ flexGrow: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Employment Details
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ width: 220, textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600}>
                                {`Form Completion: ${completion}%`}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={completion}
                                sx={{ height: 8, borderRadius: 5, mt: 0.5, backgroundColor: '#e0e0e0' }}
                                color={completion === 100 ? 'success' : 'primary'}
                            />
                        </Box>
                    </Stack>
                </Stack>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <DatePicker
                            label="Date of Joining"
                            value={formData.dateOfJoining}
                            onChange={(newValue) => {
                                setFormData(prev => ({ ...prev, dateOfJoining: newValue }));
                                setErrors(prev => ({ ...prev, dateOfJoining: false }));
                            }}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    required: true,
                                    error: !!errors.dateOfJoining,
                                    helperText: errors.dateOfJoining
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Autocomplete
                            options={employmentTypes}
                            getOptionLabel={(option) => option.name || ""}
                            value={getOptionById(employmentTypes, formData.employmentTypeId)}
                            onChange={(e, val) => handleAutocompleteChange('employmentTypeId', val)}
                            renderInput={(params) => (
                                <TextField {...params} label="Employment Type" required size="small"
                                    error={!!errors.employmentTypeId} helperText={errors.employmentTypeId} />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Autocomplete
                            options={roles}
                            getOptionLabel={(option) => option.name || ""}
                            value={getOptionById(roles, formData.roleId)}
                            onChange={(e, val) => handleAutocompleteChange('roleId', val)}
                            renderInput={(params) => (
                                <TextField {...params} label="User Role" required size="small"
                                    error={!!errors.roleId} helperText={errors.roleId} />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Autocomplete
                            options={departments}
                            getOptionLabel={(option) => option.department || ""}
                            value={getOptionById(departments, formData.department)}
                            loading={loadingDepts}
                            onChange={(e, val) => handleAutocompleteChange('department', val)}
                            renderInput={(params) => (
                                <TextField {...params} label="Department" required size="small"
                                    error={!!errors.department} helperText={errors.department} />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Autocomplete
                            options={teams}
                            getOptionLabel={(option) => option.team || ""}
                            value={getOptionById(teams, formData.team)}
                            disabled={!formData.department}
                            onChange={(e, val) => handleAutocompleteChange('team', val)}
                            renderInput={(params) => <TextField {...params} label="Team" size="small" />}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Autocomplete
                            options={designations}
                            getOptionLabel={(option) => option.designation || ""}
                            value={getOptionById(designations, formData.designation)}
                            disabled={!formData.team}
                            onChange={(e, val) => handleAutocompleteChange('designation', val)}
                            renderInput={(params) => (
                                <TextField {...params} label="Designation" required size="small"
                                    error={!!errors.designation} helperText={errors.designation} />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Autocomplete
                            options={managersList}
                            getOptionLabel={(option) => option.name || ""}
                            value={getOptionById(managersList, formData.reportingManager)}
                            onChange={(e, val) => handleAutocompleteChange('reportingManager', val)}
                            renderInput={(params) => (
                                <TextField {...params} label="Reporting Manager" required size="small"
                                    error={!!errors.reportingManager} helperText={errors.reportingManager} />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Autocomplete
                            options={locations}
                            getOptionLabel={(option) => option.location_name || ""}
                            value={getOptionById(locations, formData.workLocation)}
                            onChange={(e, val) => handleAutocompleteChange('workLocation', val)}
                            renderInput={(params) => (
                                <TextField {...params} label="Work Location" required size="small"
                                    error={!!errors.workLocation} helperText={errors.workLocation} />
                            )}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" onClick={handleSaveChanges} sx={{ px: 4, textTransform: 'none' }}>
                        {navigationContext?.returnToRehire ? "Verify & Return" : "Save Changes"}
                    </Button>
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
}

export default EmploymentDetails;